import React, { useState } from "react"
import { Button, Checkbox, Modal, Select, Tooltip } from "antd"
import pencilIcon from "assets/svg/pencil.svg"
import trashIcon from "assets/svg/trash.svg"
import chevronUpIcon from "assets/svg/chevron-up.svg"
import chevronDownIcon from "assets/svg/chevron-down.svg"
import xIcon from "assets/svg/x.svg"
import DateInput, { DateInputValue } from "../setup/components/DateInput"

// ── helpers ──────────────────────────────────────────────────────────
const getInitials = (name: string) =>
  name
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase()

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
]

const formatDate = (d?: DateInputValue): string => {
  if (!d?.dd || !d?.mm || !d?.yyyy) return "—"
  const month = MONTHS[Number(d.mm) - 1] ?? d.mm
  return `${d.dd} ${month} ${d.yyyy}`
}

// ── types ────────────────────────────────────────────────────────────
export interface ShiftPeriodOption {
  id: number
  title: string
  startTime?: string
  endTime?: string
}

export interface Employee {
  id: string
  name: string
  /** @deprecated use shiftPeriodIds.length for display */
  eligibleShifts: number | null
  /** IDs of shift periods this employee is eligible for (any-day) */
  shiftPeriodIds?: number[]
  shiftsRequired: number
  selected: boolean
}

export interface ShiftTable {
  id: string
  dateLabel: string
  endDateLabel: string
  startDate: DateInputValue
  endDate: DateInputValue
  employees: Employee[]
  collapsed: boolean
}

interface ShiftTableCardProps {
  table: ShiftTable
  /** Shift periods for eligible-shifts modal (any-day flow) */
  shiftPeriods?: ShiftPeriodOption[]
  onToggleCollapse: () => void
  onDelete: () => void
  onUpdateStartDate: (val: DateInputValue) => void
  onUpdateEndDate: (val: DateInputValue) => void
  onAddEmployee: () => void
  onToggleSelectAll: (checked: boolean) => void
  onToggleEmployee: (empId: string) => void
  onUpdateShiftsRequired: (empId: string, delta: number) => void
  onSelectEligibleShifts?: (empId: string, shiftPeriodIds: number[]) => void
  onSaveDuration?: () => boolean
}

// ── component ────────────────────────────────────────────────────────
const ShiftTableCard: React.FC<ShiftTableCardProps> = ({
  table,
  shiftPeriods = [],
  onToggleCollapse,
  onDelete,
  onUpdateStartDate,
  onUpdateEndDate,
  onAddEmployee,
  onToggleSelectAll,
  onToggleEmployee,
  onUpdateShiftsRequired,
  onSelectEligibleShifts,
  onSaveDuration,
}) => {
  const [editingDuration, setEditingDuration] = useState(false)
  const [eligibleModalOpen, setEligibleModalOpen] = useState(false)
  const [editingEligibleEmp, setEditingEligibleEmp] = useState<Employee | null>(
    null
  )
  const [selectedPeriodIds, setSelectedPeriodIds] = useState<number[]>([])

  const allSelected =
    table.employees.length > 0 && table.employees.every((e) => e.selected)

  const openEligibleModal = (emp: Employee) => {
    setEditingEligibleEmp(emp)
    setSelectedPeriodIds(emp.shiftPeriodIds ?? [])
    setEligibleModalOpen(true)
  }

  const closeEligibleModal = () => {
    setEligibleModalOpen(false)
    setEditingEligibleEmp(null)
  }

  const saveEligibleShifts = () => {
    if (editingEligibleEmp && onSelectEligibleShifts) {
      onSelectEligibleShifts(editingEligibleEmp.id, selectedPeriodIds)
    }
    closeEligibleModal()
  }

  return (
    <div className="flex gap-2 items-stretch">
      {/* left rail: line + collapse/expand toggle in the middle */}
      <div className="flex flex-col items-center w-6 shrink-0">
        {/* top line */}
        <div className="w-px bg-[#E2E8F0] flex-1" />

        {/* collapse/expand button */}
        <Tooltip title={table.collapsed ? "Expand table" : "Collapse table"}>
          <button
            type="button"
            className="p-1.5 border-2 border-[#E2E8F0] rounded-md hover:bg-[#F1F5F9] my-1"
            onClick={onToggleCollapse}
          >
            {table.collapsed ? (
              <div className="flex flex-col items-center gap-0">
                <img
                  src={chevronUpIcon}
                  width={12}
                  height={12}
                  alt=""
                />
                <img
                  src={chevronDownIcon}
                  width={12}
                  height={12}
                  alt=""
                />
              </div>
            ) : (
              <img
                src={xIcon}
                width={12}
                height={12}
                alt=""
              />
            )}
          </button>
        </Tooltip>

        {/* bottom line */}
        <div className="w-px bg-[#E2E8F0] flex-1" />
      </div>

      {/* main content */}
      <div className="flex-1 flex flex-col gap-3">
        {/* duration row */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-[#64748B]">Shift table duration:</span>
          {editingDuration ? (
            <div className="flex items-center gap-2">
              <DateInput
                value={table.startDate}
                onChange={onUpdateStartDate}
              />
              <span className="text-[#94A3B8] text-sm">to</span>
              <DateInput
                value={table.endDate}
                onChange={onUpdateEndDate}
              />
              <button
                type="button"
                className="text-sm font-semibold text-[#004AF5]"
                onClick={() => {
                  if (onSaveDuration?.() !== false) setEditingDuration(false)
                }}
              >
                Done
              </button>
            </div>
          ) : (
            <span className="flex items-center gap-1.5 text-sm font-bold text-[#0F172B]">
              {formatDate(table.startDate)} to {formatDate(table.endDate)}
              <button
                type="button"
                onClick={() => setEditingDuration(true)}
                className="text-[#64748B] hover:text-[#0F172B]"
                aria-label="Edit shift table duration"
              >
                <img
                  src={pencilIcon}
                  width={14}
                  height={14}
                  alt=""
                />
              </button>
            </span>
          )}
        </div>

        {!table.collapsed && (
          <>
            {/* toolbar */}
            <div className="flex items-center gap-3">
              <Button
                type="default"
                className="font-semibold px-4 h-9 rounded-lg border-2 border-[#004AF5] text-[#004AF5] bg-white hover:!border-[#004AF5] hover:!text-[#004AF5] hover:!bg-[#F8FAFC] flex items-center gap-1 text-sm"
                onClick={onAddEmployee}
              >
                Add employee +
              </Button>
              {shiftPeriods.length > 0 && (
                <Select
                  placeholder="Select eligible shifts for selection"
                  className="flex-1 [&_.ant-select-selector]:!h-9 [&_.ant-select-selector]:!rounded-lg [&_.ant-select-selector]:!flex [&_.ant-select-selector]:!items-center"
                  options={shiftPeriods.map((p) => ({
                    value: p.id,
                    label: p.title,
                  }))}
                  mode="multiple"
                  allowClear
                />
              )}
              <Select
                placeholder="Shifts required"
                className="w-[160px] [&_.ant-select-selector]:!h-9 [&_.ant-select-selector]:!rounded-lg [&_.ant-select-selector]:!flex [&_.ant-select-selector]:!items-center"
                options={[1, 2, 3, 4, 5, 6, 7].map((n) => ({
                  value: n,
                  label: n === 1 ? "1 shift" : `${n} shifts`,
                }))}
              />
            </div>

            {/* table header */}
            <div className="grid grid-cols-[32px_200px_1fr_auto] items-center bg-[#F8FAFC] border border-[#E2E8F0] rounded-t-lg px-4 py-2.5 text-[#64748B] font-semibold text-sm">
              <span />
              <span>Name</span>
              <span>Eligible shifts</span>
              <span className="text-right">Shifts required / week</span>
            </div>

            {/* select all row */}
            {table.employees.length > 0 && (
              <div className="grid grid-cols-[32px_200px_1fr_auto] items-center border-x border-b border-[#E2E8F0] px-4 py-3">
                <Checkbox
                  checked={allSelected}
                  onChange={(e) => onToggleSelectAll(e.target.checked)}
                />
                <span className="text-sm text-[#0F172B]">
                  Select all
                </span>
                <span />
                <span />
              </div>
            )}

            {/* employee rows */}
            {table.employees.map((emp) => (
              <div
                key={emp.id}
                className="grid grid-cols-[32px_200px_1fr_auto] items-center border-x border-b border-[#E2E8F0] px-4 py-3"
              >
                <Checkbox
                  checked={emp.selected}
                  onChange={() => onToggleEmployee(emp.id)}
                />

                {/* name cell */}
                <div className="flex items-center gap-2">
                  <span className="rounded-full bg-[#E2E8F0] w-8 h-8 flex items-center justify-center font-semibold text-xs text-[#0F172B]">
                    {getInitials(emp.name)}
                  </span>
                  <span className="text-sm text-[#0F172B]">
                    {emp.name}
                  </span>
                </div>

                {/* eligible shifts cell */}
                <div className="justify-self-start">
                  {(emp.shiftPeriodIds?.length ?? 0) > 0 ||
                  emp.eligibleShifts !== null ? (
                    <button
                      type="button"
                      className="flex items-center gap-1.5 text-sm text-[#0F172B] font-semibold hover:opacity-80"
                      onClick={() =>
                        shiftPeriods.length > 0 && openEligibleModal(emp)
                      }
                    >
                      Eligible for{" "}
                      {emp.shiftPeriodIds?.length ?? emp.eligibleShifts ?? 0}{" "}
                      shifts
                      <img
                        src={pencilIcon}
                        width={14}
                        height={14}
                        alt=""
                        className="opacity-40"
                      />
                    </button>
                  ) : (
                    <Button
                      type="default"
                      className="font-semibold px-3 h-8 rounded-[6px] border-2 border-[#E2E8F0] text-[#0F172B] text-sm flex items-center gap-1"
                      onClick={() =>
                        shiftPeriods.length > 0 && openEligibleModal(emp)
                      }
                      disabled={shiftPeriods.length === 0}
                    >
                      + Select eligible shifts
                    </Button>
                  )}
                </div>

                {/* shifts required counter (1–7) */}
                <div className="flex items-center gap-2 justify-self-end">
                  <button
                    type="button"
                    className="w-9 h-9 flex items-center justify-center border-2 border-[#E2E8F0] rounded-lg text-[#0F172B] font-semibold hover:bg-[#F1F5F9] text-base disabled:opacity-50"
                    onClick={() => onUpdateShiftsRequired(emp.id, -1)}
                    disabled={emp.shiftsRequired <= 1}
                  >
                    —
                  </button>
                  <span className="font-semibold text-sm text-[#0F172B] min-w-[20px] text-center">
                    {Math.min(7, Math.max(1, emp.shiftsRequired))}
                  </span>
                  <button
                    type="button"
                    className="w-9 h-9 flex items-center justify-center border-2 border-[#E2E8F0] rounded-lg text-[#0F172B] font-semibold hover:bg-[#F1F5F9] text-base disabled:opacity-50"
                    onClick={() => onUpdateShiftsRequired(emp.id, 1)}
                    disabled={emp.shiftsRequired >= 7}
                  >
                    +
                  </button>
                </div>
              </div>
            ))}

            {/* empty state for tables with no employees */}
            {table.employees.length === 0 && (
              <div className="border-x border-b border-[#E2E8F0] rounded-b-lg px-4 py-3">
                <div className="grid grid-cols-[32px_200px_1fr_auto] items-center">
                  <Checkbox disabled />
                  <span className="text-sm text-[#94A3B8]">
                    Select all
                  </span>
                  <span />
                  <span />
                </div>
              </div>
            )}
          </>
        )}

        {/* delete button */}
        <Button
          type="default"
          className="self-start font-semibold px-4 h-9 rounded-lg border-2 border-[#E2E8F0] text-[#0F172B] text-sm flex items-center gap-2"
          onClick={onDelete}
        >
          <img
            src={trashIcon}
            width={14}
            height={14}
            alt=""
          />
          Delete this shift table
        </Button>
      </div>

      {/* Eligible shifts modal */}
      <Modal
        title="Select eligible shifts"
        open={eligibleModalOpen}
        onCancel={closeEligibleModal}
        onOk={saveEligibleShifts}
        okText="Save"
        okType="default"
        className=""
        styles={{ content: { padding: 16 } }}
      >
        <div className="flex flex-col gap-2 py-2">
          {shiftPeriods.map((p) => (
            <label
              key={p.id}
              className="flex items-center gap-2 cursor-pointer text-sm text-[#0F172B]"
            >
              <Checkbox
                checked={selectedPeriodIds.includes(p.id)}
                onChange={(e) =>
                  setSelectedPeriodIds((prev) =>
                    e.target.checked
                      ? [...prev, p.id]
                      : prev.filter((id) => id !== p.id)
                  )
                }
              />
              <span className="font-semibold">{p.title}</span>
              {p.startTime != null && p.endTime != null && (
                <span className="text-[#64748B] text-xs">
                  {p.startTime} – {p.endTime}
                </span>
              )}
            </label>
          ))}
          {shiftPeriods.length === 0 && (
            <p className="text-[#64748B] text-sm">
              No shift periods available.
            </p>
          )}
        </div>
      </Modal>
    </div>
  )
}

export default ShiftTableCard
